import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  SupportContactRequest,
  SupportContactService,
} from 'src/app/services/support-contact.service';

type ContactStatus = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Machine values posted as `reason`. The label for each is
 * `CONTACT.REASONS.<VALUE uppercased>` in the i18n files — keep the two in
 * step when adding a reason.
 */
export const CONTACT_REASONS = [
  'sales',
  'support',
  'billing',
  'partnership',
  'press',
  'careers',
  'feedback',
  'other',
] as const;

/** Reason that gets the "you may want the job-seeker product instead" nudge. */
const CAREERS_REASON = 'careers';

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss'],
})
export class ContactUsComponent implements OnDestroy {
  private fb = inject(FormBuilder);
  private supportContact = inject(SupportContactService);

  /**
   * `website` is a honeypot: rendered offscreen, aria-hidden and untabbable, so
   * only a bot ever fills it. It is posted as-is — the backend decides.
   */
  readonly form = this.fb.group({
    reason: ['', [Validators.required]],
    name: ['', [Validators.required, Validators.maxLength(150)]],
    email: [
      '',
      [Validators.required, Validators.email, Validators.maxLength(180)],
    ],
    subject: ['', [Validators.required, Validators.maxLength(200)]],
    message: ['', [Validators.required, Validators.maxLength(5000)]],
    phone: ['', [Validators.required, Validators.maxLength(40)]],
    company: ['', [Validators.maxLength(150)]],
    website: [''],
  });

  /** `[{ value: 'sales', label: 'CONTACT.REASONS.SALES' }, …]` for the select. */
  readonly reasons = CONTACT_REASONS.map((value) => ({
    value,
    label: `CONTACT.REASONS.${value.toUpperCase()}`,
  }));

  /** Same entry point the header's "I'm a Job Seeker" button uses. */
  readonly jobSeekerLink = '/auto-apply';

  status: ContactStatus = 'idle';
  /**
   * Helpdesk ticket reference returned on success (e.g. `TKT-000123`). Stays
   * null for reasons that don't open a ticket — the success card just drops
   * the reference pill, the send still counted.
   */
  reference: string | null = null;
  /** Translate key for the inline banner — never a raw server string. */
  errorMessage: string | null = null;
  /** Field name -> already-localized 422 message from Laravel. */
  fieldErrors: { [field: string]: string } = {};

  private submission?: Subscription;

  /**
   * Editing after a rejected submit clears the banner and the (now stale)
   * server-side messages — the client validators still guide the retry.
   */
  private readonly edits = this.form.valueChanges.subscribe(() => {
    if (this.status === 'error') {
      this.status = 'idle';
      this.errorMessage = null;
      this.fieldErrors = {};
    }
  });

  get submitting(): boolean {
    return this.status === 'submitting';
  }

  /**
   * Job seekers are in the wrong place — point them at Auto-Apply. This is a
   * nudge, not a wall: the form stays submittable and their message is still
   * relayed to the team.
   */
  get showJobSeekerNudge(): boolean {
    return this.form.get('reason')?.value === CAREERS_REASON;
  }

  /** True once the control is worth complaining about (touched or dirty). */
  hasError(field: string, error: string): boolean {
    const control = this.form.get(field);
    return (
      !!control && control.hasError(error) && (control.touched || control.dirty)
    );
  }

  submit(): void {
    if (this.submitting) {
      return;
    }

    this.errorMessage = null;
    this.fieldErrors = {};

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.status = 'error';
      this.errorMessage = 'CONTACT.ERRORS.CHECK_FIELDS';
      return;
    }

    const value = this.form.getRawValue();
    const payload: SupportContactRequest = {
      reason: value.reason ?? '',
      name: (value.name ?? '').trim(),
      email: (value.email ?? '').trim(),
      subject: (value.subject ?? '').trim(),
      message: (value.message ?? '').trim(),
      phone: (value.phone ?? '').trim(),
      company: (value.company ?? '').trim(),
      website: value.website ?? '',
    };

    this.status = 'submitting';
    this.submission?.unsubscribe();
    this.submission = this.supportContact.submit(payload).subscribe({
      next: (res) => {
        this.reference = res?.data?.reference ?? null;
        this.status = 'success';
      },
      // The form keeps its values on failure so nothing has to be retyped.
      error: (err: HttpErrorResponse) => this.handleError(err),
    });
  }

  /** Back to an empty form after a successful send. */
  sendAnother(): void {
    this.form.reset({
      reason: '',
      name: '',
      email: '',
      subject: '',
      message: '',
      phone: '',
      company: '',
      website: '',
    });
    this.status = 'idle';
    this.reference = null;
    this.errorMessage = null;
    this.fieldErrors = {};
  }

  ngOnDestroy(): void {
    this.submission?.unsubscribe();
    this.edits.unsubscribe();
  }

  private handleError(err: HttpErrorResponse): void {
    this.status = 'error';

    // The helpdesk API nests validation under `data.fields`; `errors` is the
    // plain-Laravel shape, kept as a fallback. Both are already localized by
    // the Accept-Language header the global interceptor sends.
    const errors = err?.error?.data?.fields ?? err?.error?.errors;
    if (err?.status === 422 && errors && typeof errors === 'object') {
      Object.keys(errors).forEach((field) => {
        const messages = errors[field];
        const message = Array.isArray(messages) ? messages[0] : messages;
        if (typeof message === 'string' && message) {
          this.fieldErrors[field] = message;
        }
      });
      this.errorMessage = 'CONTACT.ERRORS.CHECK_FIELDS';
      return;
    }

    if (err?.status === 429) {
      this.errorMessage = 'CONTACT.ERRORS.TOO_MANY';
      return;
    }

    this.errorMessage = 'CONTACT.ERRORS.GENERIC';
  }
}
